import { Button, notification, Spin, Table } from 'antd'
import React, { useMemo, useState } from 'react'
import {
  useCreateReleaseNoteMutation,
  useFetchReleaseNotesQuery,
  useLazyFetchReleaseNoteByIdQuery,
  usePublishReleaseNoteMutation,
  useUpdateReleaseNoteMutation,
} from 'domain/ReleaseNotes/releaseNotesApi'
import Card from 'components/Card'
import dayjs from 'dayjs'
import Heading from 'components/Heading'
import { useTranslation } from 'react-i18next'

import ReleaseNotesDrawer from './ReleaseNotesDrawer'

import '../../global/styles/global.less'
import './ReleaseNotes.less'

const ReleaseNotes = () => {
  const { t } = useTranslation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState('create')
  const [drawerInitialValues, setDrawerInitialValues] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [isFetchingEditDetail, setIsFetchingEditDetail] = useState(false)

  const { data: releaseNotesResponse, isLoading: isFetchingReleaseNotes } =
    useFetchReleaseNotesQuery()

  const releaseNotesList = useMemo(
    () =>
      [...(releaseNotesResponse?.data ?? [])].sort((a, b) => {
        if (a.published !== b.published) return a.published ? 1 : -1

        return dayjs(b.releaseDate).valueOf() - dayjs(a.releaseDate).valueOf()
      }),
    [releaseNotesResponse?.data]
  )

  const [triggerCreateReleaseNote, { isLoading: isCreating }] =
    useCreateReleaseNoteMutation()

  const [triggerUpdateReleaseNote, { isLoading: isUpdating }] =
    useUpdateReleaseNoteMutation()

  const [triggerPublishReleaseNote, { isLoading: isPublishing }] =
    usePublishReleaseNoteMutation()

  const [fetchReleaseNoteById] = useLazyFetchReleaseNoteByIdQuery()

  const openCreateDrawer = () => {
    setDrawerMode('create')
    setDrawerInitialValues(null)
    setDrawerOpen(true)
  }

  const openEditDrawer = async record => {
    setIsFetchingEditDetail(true)
    try {
      const response = await fetchReleaseNoteById(record.id).unwrap()
      const detail = response?.data ?? response

      setDrawerMode('edit')
      setDrawerInitialValues({
        notes: detail.notes ?? [],
        releaseDate: detail.releaseDate ? dayjs(detail.releaseDate) : null,
        titleVersion: detail.title ?? '',
      })
      setEditingId(detail.id ?? record.id)
      setDrawerOpen(true)
    } catch {
      notification.error({
        message: t('global.error.getRequestErrorMessage'),
      })
    } finally {
      setIsFetchingEditDetail(false)
    }
  }

  const closeDrawer = () => setDrawerOpen(false)

  const buildPayload = values => ({
    notes: values.notes,
    releaseDate: values.releaseDate
      ? dayjs(values.releaseDate).format('YYYY-MM-DDTHH:mm:ss')
      : undefined,
    title: values.titleVersion,
  })

  const handlePublish = async values => {
    try {
      let idToPublish

      if (drawerMode === 'edit') {
        await triggerUpdateReleaseNote({
          id: editingId,
          ...buildPayload(values),
        }).unwrap()
        idToPublish = editingId
      } else {
        const createResponse = await triggerCreateReleaseNote(
          buildPayload(values)
        ).unwrap()
        idToPublish = createResponse?.data?.id ?? createResponse?.id
      }

      await triggerPublishReleaseNote(idToPublish).unwrap()

      notification.success({
        message: t('releaseNotes.notifications.publishSuccess'),
      })
      closeDrawer()
    } catch {
      notification.error({
        message: t('global.error.postRequestErrorDescription'),
      })
    }
  }

  const handleSave = async values => {
    const payload = buildPayload(values)

    try {
      if (drawerMode === 'edit') {
        await triggerUpdateReleaseNote({ id: editingId, ...payload }).unwrap()
      } else {
        await triggerCreateReleaseNote(payload).unwrap()
      }

      notification.success({
        message: t('releaseNotes.notifications.saveSuccess'),
      })
      closeDrawer()
    } catch {
      notification.error({
        message: t('global.error.postRequestErrorDescription'),
      })
    }
  }

  const columns = [
    {
      dataIndex: 'title',
      key: 'title',
      title: t('releaseNotes.table.releaseVersion'),
    },
    {
      dataIndex: 'releaseDate',
      key: 'releaseDate',
      render: value => (value ? dayjs(value).format('DD-MM-YYYY') : '-'),
      title: t('releaseNotes.table.date'),
    },
    {
      dataIndex: 'published',
      key: 'status',
      render: published =>
        published ? (
          <span className="release-notes__status--published">
            {t('releaseNotes.table.published')}
          </span>
        ) : (
          <span className="release-notes__status--draft">
            {t('releaseNotes.table.draft')}
          </span>
        ),
      title: t('releaseNotes.table.status'),
    },
    {
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => (
        <Button
          disabled={record.published === true}
          onClick={() => openEditDrawer(record)}
          size="small"
          type="link"
        >
          {t('releaseNotes.table.edit')}
        </Button>
      ),
      title: t('releaseNotes.table.action'),
    },
  ]

  return (
    <>
      {(isFetchingEditDetail || isCreating || isUpdating || isPublishing) && (
        <div className="release-notes__loading-overlay">
          <Spin size="large" />
        </div>
      )}
      <div className="page__container">
        <Heading level={1}>{t('releaseNotes.pageTitle')}</Heading>

        <Card
          title={t('releaseNotes.table.tableTitle')}
          headerActionButton={{
            label: t('releaseNotes.create'),
            onClick: openCreateDrawer,
          }}
        >
          <Spin spinning={isFetchingReleaseNotes}>
            <Table
              columns={columns}
              dataSource={releaseNotesList}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              rowKey="id"
            />
          </Spin>
        </Card>

        <ReleaseNotesDrawer
          initialValues={drawerInitialValues}
          mode={drawerMode}
          onClose={closeDrawer}
          onPublish={handlePublish}
          onSave={handleSave}
          open={drawerOpen}
        />
      </div>
    </>
  )
}

export default ReleaseNotes
